// ===========================================
// Retention Chart Component
// ===========================================
// Horizontal bar chart showing top lessons by retention rate
// Phase 2 Priority 12: Reporting & Analytics - Week 4

import React from 'react';
import { Paper, Typography, Box, Skeleton } from '@mui/material';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Cell,
} from 'recharts';
import { RetentionRecord } from '../../api/reports.api';

interface RetentionChartProps {
  data?: RetentionRecord[];
  isLoading: boolean;
}

const getRetentionColor = (retentionRate: number): string => {
  if (retentionRate >= 85) return '#5cb399'; // Green
  if (retentionRate >= 70) return '#FFCE00'; // Yellow
  return '#FFAE9E'; // Coral/Red
};

const RetentionChart: React.FC<RetentionChartProps> = ({ data, isLoading }) => {
  if (isLoading) {
    return (
      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" gutterBottom>
          Retention Rates by Lesson
        </Typography>
        <Skeleton variant="rectangular" height={400} />
      </Paper>
    );
  }

  if (!data || data.length === 0) {
    return (
      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" gutterBottom>
          Retention Rates by Lesson
        </Typography>
        <Box sx={{ textAlign: 'center', py: 8 }}>
          <Typography variant="body2" color="text.secondary">
            No retention data available
          </Typography>
        </Box>
      </Paper>
    );
  }

  // Take top 10 lessons by retention rate
  const chartData = data
    .sort((a, b) => b.retentionRate - a.retentionRate)
    .slice(0, 10)
    .map((lesson) => ({
      name: lesson.lessonName.length > 30 ? lesson.lessonName.slice(0, 30) + '...' : lesson.lessonName,
      retentionRate: lesson.retentionRate,
      active: lesson.activeEnrollments,
      total: lesson.totalEnrollments,
    }));

  return (
    <Paper sx={{ p: 3, mb: 3 }}>
      <Typography variant="h6" gutterBottom>
        Top 10 Lessons by Retention Rate
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Percentage of students who remain enrolled (active enrollments / total enrollments)
      </Typography>
      <ResponsiveContainer width="100%" height={400}>
        <BarChart data={chartData} layout="vertical">
          <CartesianGrid strokeDasharray="3 3" />
          <XAxis type="number" domain={[0, 100]} />
          <YAxis dataKey="name" type="category" width={150} />
          <Tooltip
            content={({ payload }) => {
              if (payload && payload.length > 0) {
                const data = payload[0].payload;
                return (
                  <Box
                    sx={{
                      bgcolor: 'background.paper',
                      p: 1.5,
                      border: '1px solid',
                      borderColor: 'divider',
                      borderRadius: 1,
                    }}
                  >
                    <Typography variant="subtitle2">{data.name}</Typography>
                    <Typography variant="body2">
                      Retention Rate: {data.retentionRate.toFixed(1)}%
                    </Typography>
                    <Typography variant="caption" display="block">
                      Active: {data.active} / {data.total}
                    </Typography>
                  </Box>
                );
              }
              return null;
            }}
          />
          <Bar dataKey="retentionRate" name="Retention Rate (%)">
            {chartData.map((entry, index) => (
              <Cell key={`cell-${index}`} fill={getRetentionColor(entry.retentionRate)} />
            ))}
          </Bar>
        </BarChart>
      </ResponsiveContainer>

      {/* Color Legend */}
      <Box sx={{ display: 'flex', gap: 3, mt: 2, justifyContent: 'center' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Box sx={{ width: 20, height: 20, bgcolor: '#5cb399', borderRadius: 0.5 }} />
          <Typography variant="caption">Excellent (85%+)</Typography>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Box sx={{ width: 20, height: 20, bgcolor: '#FFCE00', borderRadius: 0.5 }} />
          <Typography variant="caption">Good (70-85%)</Typography>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Box sx={{ width: 20, height: 20, bgcolor: '#FFAE9E', borderRadius: 0.5 }} />
          <Typography variant="caption">Needs Improvement (&lt;70%)</Typography>
        </Box>
      </Box>
    </Paper>
  );
};

export default RetentionChart;
